import React, { useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CategoriesPicker } from '../../ui/business/CategoriesPicker';
import { CategoryUI } from '../../ui/business/types';
import { Dialog, DialogContent, DialogFooter, DialogTitle } from '../../ui/feedback/Dialog';
import { Button } from '../../ui/input/Button';
import { Flex } from '../../ui/layout/Flex';
import { Typography } from '../../ui/typography/Typography';
import { useTranslation } from '../../utils/useTranslation';

export const getUpdatedTree = (
    categoryId: number,
    newState: boolean,
    selectedCategoriesTree?: CategoryUI[],
): CategoryUI[] | undefined =>
    selectedCategoriesTree
        ? selectedCategoriesTree.map((category) => {
              const updatedCategory = { ...category };
              if (category.id === categoryId) {
                  updatedCategory.checked = newState;
                  updatedCategory.indeterminate = false;

                  if (updatedCategory.children?.length) {
                      updatedCategory.children.forEach((childCategory) => {
                          updatedCategory.children = getUpdatedTree(
                              childCategory.id,
                              newState,
                              updatedCategory.children,
                          );
                      });
                  }
              } else if (updatedCategory.children) {
                  updatedCategory.children.forEach(() => {
                      updatedCategory.children = getUpdatedTree(
                          categoryId,
                          newState,
                          updatedCategory.children,
                      );
                  });
                  const checkedChildrenCount = updatedCategory.children.filter(
                      (childCategory) => childCategory.checked,
                  ).length;
                  if (checkedChildrenCount === updatedCategory.children.length) {
                      updatedCategory.checked = true;
                      updatedCategory.indeterminate = false;
                  } else if (checkedChildrenCount === 0) {
                      updatedCategory.checked = false;
                      updatedCategory.indeterminate = false;
                  } else {
                      updatedCategory.checked = false;
                      updatedCategory.indeterminate = true;
                  }
              }

              return updatedCategory;
          })
        : undefined;

export function SelectCategoriesDialog() {
    const t = useTranslation();
    const navigate = useNavigate();
    const { state } = useLocation();
    const { draftBusinessCategoriesTree, saveLink } = state;
    const [selectedCategoriesTree, setSelectedCategoriesTree] = useState<CategoryUI[]>(
        draftBusinessCategoriesTree,
    );
    const updateCategoryState = useCallback(
        (categoryId: number, newState: boolean) => {
            const updatedSelectedCategoriesTree = getUpdatedTree(
                categoryId,
                newState,
                selectedCategoriesTree,
            );
            if (updatedSelectedCategoriesTree) {
                setSelectedCategoriesTree(updatedSelectedCategoriesTree);
            }
        },
        [selectedCategoriesTree, setSelectedCategoriesTree],
    );

    const onSaveClick = useCallback(() => {
        navigate(saveLink, { state: { selectedCategoriesTree } });
    }, [selectedCategoriesTree, navigate, saveLink]);

    const onClose = useCallback(() => {
        navigate(saveLink);
    }, [navigate, saveLink]);

    return (
        <Dialog height="auto" borderRadius={{ xs: 0, sm: 3 }} maxWidth={{ xs: '100%', sm: 580 }}>
            <DialogTitle onClose={onClose} overflow="visible">
                <Typography variant="title3" weight="medium">
                    {t('Категории контента')}
                </Typography>
            </DialogTitle>
            <DialogContent py={2} px={2}>
                <CategoriesPicker
                    selectedCategoriesTree={selectedCategoriesTree}
                    updateCategoryState={updateCategoryState}
                />
            </DialogContent>
            <DialogFooter overflow="visible">
                <Flex direction="row" justifyContent="center" spacing={1} width="100%">
                    <Button variant="filled" color="primary" width="100%" onClick={onSaveClick}>
                        {t('Сохранить')}
                    </Button>
                </Flex>
            </DialogFooter>
        </Dialog>
    );
}
